/**
 * Client-side file download helpers.
 *
 * The backend returns downloadable material (PEM certificates, keys, CA
 * bundles) as JSON strings; the actual files are built browser-side via a
 * Blob + temporary object URL. Keep the content out of any persistent state:
 * call {@link downloadTextFile} with a locally-scoped string and let it go
 * out of scope afterwards (relevant for private key material).
 */

const PEM_MIME_TYPE = 'application/x-pem-file';

/**
 * Turn a free-form name (certificate CN, platform name, CA descr) into a
 * filesystem-safe filename stem.
 *
 * - lowercases,
 * - maps the wildcard marker `*` to the word `wildcard` (so `*.example.com`
 *   becomes `wildcard.example.com`, not a hidden/empty name),
 * - keeps letters, digits, dots and dashes; every other run of characters
 *   collapses to a single dash,
 * - trims leading/trailing dashes and dots.
 *
 * Falls back to `'download'` when nothing usable remains.
 */
export function slugifyForFilename(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/\*+/g, 'wildcard')
    .replace(/[^a-z0-9.-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
  return slug === '' ? 'download' : slug;
}

/**
 * Save a text payload as a file via a client-side Blob. Defaults to the PEM
 * MIME type since certificate material is this helper's primary use case.
 */
export function downloadTextFile(filename: string, content: string, mimeType: string = PEM_MIME_TYPE): void {
  const blob = new Blob([content], { type: mimeType });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.URL.revokeObjectURL(url);
}
